import React, { useState, useRef, useEffect } from 'react';

import { motion, AnimatePresence } from 'framer-motion';

import { Icon } from '../icon';

interface DrawerProps {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  hideCloseButton?: boolean;
}

// Store width outside component to persist across open/close cycles
let lastWidth = window.innerWidth * 0.6; // 60% default

export function Drawer({ isOpen, onClose, children, hideCloseButton = false }: DrawerProps) {
  const [width, setWidth] = useState(lastWidth);
  const [isResizing, setIsResizing] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      const maxWidth = window.innerWidth * 0.9;
      const minWidth = window.innerWidth * 0.3;

      const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      lastWidth = constrainedWidth;
      setWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-gray-500"
            onClick={onClose}
          />
          <motion.div
            ref={drawerRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className={`fixed top-0 right-0 z-50 h-full border-l ${isResizing ? 'border-teal-400' : 'border-gray-400'} bg-white shadow-lg transition-colors duration-300`}
            style={{ width: `${width}px` }}
          >
            {/* Resize handle */}
            <div onMouseDown={handleMouseDown} className="absolute top-0 -left-2.5 z-40 h-full w-5 cursor-ew-resize">
              <div
                className={`absolute top-1/2 left-1/2 z-20 flex h-7 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-0.75 rounded-md border border-gray-300 duration-150 hover:bg-gray-100 ${isResizing ? 'bg-gray-100' : 'bg-white'}`}
              >
                <Icon className="lucide--chevrons-left-right text-xs text-gray-500" />
              </div>
            </div>

            {!hideCloseButton && (
              <button
                onClick={onClose}
                className="absolute top-4 right-4 cursor-pointer border-none bg-transparent text-2xl"
              >
                &times;
              </button>
            )}

            <div className="h-full">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
